import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/Edit";
import MarkEmailRead from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnread from "@mui/icons-material/MarkEmailUnread";
import PendingIcon from "@mui/icons-material/Pending";
import PublicIcon from "@mui/icons-material/Public";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { green, lightBlue } from "@mui/material/colors";
import { alpha, styled } from "@mui/material/styles";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import parse from "html-react-parser";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { getIndividualByUserId } from "marketplace/individuals/_stores/individuals-redux-slice";
import ViewIndividualProfile from "marketplace/individuals/profiles/_views/individual-profile-container";
import { messagingApi } from "messaging/_api/messaging-api";
import MessageHistoryControl from "messaging/_views/components/message-history-dialog";
import { enqueueSnackbar } from "notistack";
import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
import {
  addComment,
  deleteComment,
  editComment,
  publishComment,
  unpublishComment,
} from "work/proposals/comments/_store/comments-redux-slice";
import Comment from "work/proposals/comments/comment";
import { addCommentThread } from "work/proposals/comments/threads/_store/comment-thread-redux-slice";

const SystemCommentContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ theme, unread }) => ({
  "@container (min-width: 800px)": {
    width: "75%",
  },
  "@container (min-width: 0px)": {
    width: "100%",
  },
  margin: theme.spacing(0, "auto", 4, "auto"),
  whiteSpace: "normal",
  width: "80%",

  cursor: unread ? "pointer" : undefined,
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest,
  }),

  "&:hover .system-message-content": {
    backgroundColor: unread ? alpha(theme.palette.primary.main, 0.14) : undefined,
  },
}));
const SystemCommentInfo = styled("div")(({ theme }) => ({
  alignItems: "start",
  display: "flex",
  justifyContent: "space-between",
  marginBottom: theme.spacing(0.5),
}));
const SystemCommentTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.8em",
  marginLeft: theme.spacing(1.32),
}));
const SystemCommentDateContainer = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.75),
}));
const EditCommentInput = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    "& fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&:hover fieldset": {
      borderColor: theme.palette.common.black,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.common.black,
    },
  },
}));
/**
 * A comment's row. An unread one keeps the white of the pane; a read one recedes into gray (#5127).
 *
 * The distinction used to be an 8px dot and a half-step of font weight, which is as much as a reader
 * gets to tell the two apart while scanning a whole conversation — and the dot is drawn beside the
 * author's name rather than at the row's edge, so it reads as punctuation on one line rather than as a
 * fact about the row. The background says it about the whole row, at any distance.
 *
 * Which way round is deliberate: the conversation dims behind what is still live in it, so the rows
 * left standing at full brightness are the ones with something to read. Shading the unread ones
 * instead marks the new comments as the settled ones, which is the wrong way for a pane whose whole
 * job is to show what has just arrived.
 *
 * The hover shade is a step darker rather than the tint it was: an unread row is a button (it marks
 * itself read when clicked), and a hover that lands lighter than the row's own background reads as the
 * row going out rather than as the pointer landing on it.
 */
const CommentListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "pending" && prop !== "unread",
})<{ pending: boolean; unread: boolean }>(({ pending, theme, unread }) => ({
  backgroundColor: unread ? theme.palette.background.paper : theme.palette.grey[200],
  cursor: unread ? "pointer" : undefined,
  opacity: pending ? 0.66 : 1,
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest,
  }),

  "&:hover": {
    backgroundColor: unread ? theme.palette.grey[100] : undefined,
  },

  "& .MuiTypography-root": {
    fontWeight: unread ? 500 : "normal",
  },
}));
const NameLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>(({ disabled }) => ({
  display: "inline-block",
  cursor: disabled ? "default" : "pointer",
  textDecoration: disabled ? "none" : "underline",
}));
const AvatarButton = styled(IconButton)(() => ({
  padding: 0,
}));
const NameContainer = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(1),
  marginRight: theme.spacing(1),
}));
const StatusIndicators = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));
const StatusDot = styled("span", {
  shouldForwardProp: (prop) => prop !== "color" && prop !== "alert",
})<{ color: "pending" | "unread"; alert?: "error" }>(({ theme, color, alert }) => {
  let backgroundColor = theme.palette.secondary.main;
  if (color === "pending") {
    backgroundColor = alert === "error" ? theme.palette.error.main : theme.palette.warning.main;
  }

  return {
    backgroundColor,
    borderRadius: "50%",
    display: "inline-block",
    height: "8px",
    width: "8px",
  };
});
const CommentDate = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  marginBottom: theme.spacing(0.5),
}));
const CommentStatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<{ statusColor?: "error" | "info" | "warning" }>(({ theme, statusColor }) => ({
  border: 0,
  ...(statusColor
    ? {
        color: theme.palette[statusColor].main,
        borderColor: theme.palette[statusColor].main,
      }
    : {}),
  "& .MuiChip-label": {
    alignItems: "center",
    display: "inline-flex",
    gap: theme.spacing(0.25),
    padding: 0,
  },
  "& .comment-status-chip-icons": {
    alignItems: "center",
    display: "inline-flex",
  },
  "& .MuiSvgIcon-root": {
    color: statusColor ? theme.palette[statusColor].main : undefined,
    fontSize: "1.125rem",
  },
}));
const CommentContent = styled("span")(({ theme }) => ({
  boxSizing: "border-box",
  display: "block",
  paddingRight: theme.spacing(2),
  whiteSpace: "normal",
  width: "100%",
  "& span.action": {
    fontWeight: 500,
    "&.accepted": {
      color: theme.palette.success.main,
    },
    "&.rejected": {
      color: theme.palette.error.main,
    },
  },
  "& > span.comment-redline": {
    width: "100%",
    "& > span.change-group": {
      whiteSpace: "nowrap",
      "& .added": {
        "&.resolved": {
          color: theme.palette.text.primary,
        },
        backgroundColor: "unset",
        color: theme.palette.text.primary,
      },
    },
    "& span.added": {
      backgroundColor: green[300],
      color: theme.palette.text.primary,
      "&.resolved": {
        backgroundColor: "unset",
        color: lightBlue[700],
      },
    },
    "& span.removed": {
      textDecoration: "line-through",
      textDecorationColor: theme.palette.error.main,
      "&.resolved": {
        textDecorationColor: lightBlue[700],
      },
    },
    "& span.unchanged": {
      color: theme.palette.text.primary,
    },
  },
}));
/**
 * The body of a withheld comment — one deleted, or one its author retracted. It keeps `CommentContent`'s
 * block layout and right padding so a tombstone occupies the same place in the list as the comment it
 * replaces, and reads as absence rather than as something someone wrote: italic, in the muted secondary
 * text colour.
 */
const WithheldCommentText = styled("span")(({ theme }) => ({
  boxSizing: "border-box",
  color: theme.palette.text.secondary,
  display: "block",
  fontStyle: "italic",
  paddingRight: theme.spacing(2),
  whiteSpace: "normal",
  width: "100%",
}));
const SystemMessageContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "unread",
})<{ unread: boolean }>(({ theme, unread }) => ({
  // The same read/unread contrast the comment rows carry (#5127), moved one step so a banner that is
  // already gray keeps a visible box in both states: an unread one lifts towards the pane, and a read
  // one settles into the shade it has always had. Without this a system message was the one row in the
  // list whose two states were the same color.
  backgroundColor: unread ? theme.palette.grey[100] : theme.palette.grey[300],
  borderRadius: theme.spacing(0.66),
  color: unread ? theme.palette.text.primary : theme.palette.text.secondary,
  display: "block",
  fontWeight: unread ? 500 : undefined,
  textAlign: "center",
  padding: theme.spacing(2),
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shortest,
  }),
  "& > p": {
    margin: 0,
  },
}));

/**
 * What a row's audience switch is drawn against: the comment as the row's open edit currently aims it,
 * and how to record the author aiming it somewhere else.
 *
 * The row hands these out rather than being handed a finished element, because the audience on offer
 * belongs to an edit only the row holds. The surfaces that supply the switch own the `Proposal` it
 * needs and nothing else.
 */
export type CommentAudienceSwitchSlot = {
  comment: Comment;
  onCommentReaimed: (comment: Comment) => void;
};

/**
 * The unsaved edit a row is holding: the comment as its author has re-aimed it, and the text they have
 * rewritten. Neither is anyone else's until they save, so both live here rather than in the store.
 *
 * The draft used to be the shown comment's own `CommentThread`, written in place by the audience
 * switch. A comment and its thread are immutable — every change returns a new object — so that write
 * was invisible to React, and, because one thread instance is shared by every comment in the
 * conversation, it could re-aim rows the author never touched.
 */
type OpenCommentEdit = {
  draft: Comment;
  text: string;
};

type CommentItemProps = {
  className?: string;
  comment: Comment;
  /** Omitted by a read-only surface, which offers no audience choice at all. */
  renderAudienceSwitch?: (slot: CommentAudienceSwitchSlot) => React.ReactNode;
  readOnly?: boolean;
};

const userIdRegEx = new RegExp(
  /userId:\s?([a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12})/
);

export default function CommentItem(props: Readonly<CommentItemProps>) {
  const { className, comment, renderAudienceSwitch } = props;
  const readOnly = props.readOnly ?? false;

  const [openEdit, setOpenEdit] = React.useState<OpenCommentEdit | null>(null);
  const [isUpdatingReadStatus, setIsUpdatingReadStatus] = React.useState(false);
  const [isChangingPublishState, setIsChangingPublishState] = React.useState(false);
  // Every comment action writes the comment as it is performed, so each one owns an in-flight flag: the
  // row's controls are disabled while its own write is on the wire rather than while a proposal saves.
  const [isPersistingEdit, setIsPersistingEdit] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const commenter = useSelector(getIndividualByUserId(comment.senderId ?? undefined));

  const { openDialog } = useDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const commentUserId: Guid | undefined = useMemo(() => {
    const userIdMatch = userIdRegEx.exec(comment.content);
    if (!userIdMatch) return;
    return new Guid(userIdMatch[1]);
  }, [comment.content]);
  const commentUser = useSelector(getIndividualByUserId(commentUserId));

  const individualUserIds = new Set([
    ...(comment.senderId ? [comment.senderId.value] : []),
    ...(commentUserId ? [commentUserId.value] : []),
  ]);
  useGetIndividualsByUserIdsQuery({ userIds: individualUserIds }, { skip: individualUserIds.size === 0 });

  function getCommentContent(comment: Comment) {
    // A withheld comment keeps its place in the thread rather than vanishing out of it, so the row still
    // renders — as a tombstone. There is nothing to fall back to if these branches are missed: the
    // server hydrates a withheld comment from a type with no body, so `content` is empty by the time it
    // is here. Attachments are indicated and never presented; the tombstone carries only how many there
    // were.
    if (comment.isDeleted) {
      return (
        <WithheldCommentText data-testid="deleted-comment-tombstone">
          This comment was deleted
          {comment.attachmentCount > 0 && (
            <span data-testid="deleted-comment-attachment-count">
              {` (${comment.attachmentCount} attachment${comment.attachmentCount === 1 ? "" : "s"})`}
            </span>
          )}
        </WithheldCommentText>
      );
    }

    // A retraction used to be a disappearance: the comment left the audience's screen with nothing said
    // about it. It leaves this instead, in the place the comment held — `published` is write-once, so
    // the row has not moved — telling the reader that what they had read was taken back, and when.
    if (comment.isRetractedTombstone) {
      return (
        <WithheldCommentText data-testid="retracted-comment-tombstone">
          {`Removed at ${comment.retracted?.format("MMM D, YYYY h:mm A") ?? ""}`}
          {comment.attachmentCount > 0 && (
            <span data-testid="retracted-comment-attachment-count">
              {` (${comment.attachmentCount} attachment${comment.attachmentCount === 1 ? "" : "s"})`}
            </span>
          )}
        </WithheldCommentText>
      );
    }

    let content: string = comment.content;

    if (commentUser && comment.content) {
      content = content.replace(userIdRegEx, commentUser.fullName);
    }
    return <CommentContent>{parse(content)}</CommentContent>;
  }

  function handleViewCommenterProfile() {
    if (!commenter) {
      console.warn("Commenter not found");
      return;
    }

    openDialog({
      component: <ViewIndividualProfile profile={commenter.profile} />,
      titleStyle: {
        paddingBottom: 0,
      },
      contentSxProps: {
        display: "flex",
        overflowX: "hidden",
      },
      MuiProps: {
        maxWidth: "lg",
        fullWidth: true,
      },
    });
  }

  const isOwnComment = Boolean(comment.senderId?.isEqualTo(session.user?.id));
  const isRemoved = comment.isDeleted || Boolean(comment.retracted);
  const historyLabel = comment.isDeleted ? "Deleted" : comment.retracted ? "Removed" : comment.updated ? "Edited" : undefined;
  // Removed-message history is visible only to the author. Everyone else already has the tombstone in
  // the comment body, so repeating Deleted or Removed beside the timestamp adds no useful information.
  // The message-history endpoint enforces the same author boundary.
  const canShowHistoryStatus = Boolean(
    historyLabel &&
      comment.senderId &&
      comment.id &&
      !comment.markedForCreation &&
      (!isRemoved || isOwnComment),
  );
  const canViewHistory = Boolean(
    canShowHistoryStatus &&
      comment.thread.id,
  );
  const messageForHistory = canShowHistoryStatus
    ? comment.toMessage({ isViewingAsVendor: session.context?.viewingAsVendor ?? false })
    : undefined;
  // A tombstone is never unread. You cannot have unread content that no longer exists, and the server
  // agrees: a deleted message is excluded from `Forum.NumUnreadMessages`, so treating one as unread here
  // would put a dot on a row that no badge counts and offer a Mark Read that writes to a deleted row.
  // `Comment.isUnreadBy` states that rule once, so this row and the pane's Mark all read agree.
  const isUnread = Boolean(
    !comment.isDeleted &&
      !comment.isRetractedTombstone &&
      ((!isOwnComment && comment.isUnreadBy(session.user?.id)) || comment.markedForUnread)
  );
  // Nor is it pending: whatever was outstanding about the comment stopped mattering when it was
  // deleted. `Comment.isPending` says so itself, so the row and the comment button's badge cannot
  // disagree about whether a tombstone is undelivered work.
  const showPendingIndicator = comment.isPending;
  const isPendingExternalComment = Boolean(showPendingIndicator && comment.thread.isExternal);
  const commentStatusItems = [
    ...(comment.thread.isExternal
      ? [
          {
            icon: <PublicIcon data-testid="external-comment-status-icon" key="external-icon" />,
            key: "external",
            label: "External",
          },
        ]
      : []),
    ...(showPendingIndicator
      ? [
          {
            icon: <PendingIcon data-testid="pending-comment-status-icon" key="pending-icon" />,
            key: "pending",
            label: getPendingStatus(),
          },
        ]
      : []),
  ];
  const statusChipColor = isPendingExternalComment ? "error" : showPendingIndicator ? "warning" : undefined;

  async function handleReadStatusChanged(updatedComment: Comment) {
    if (!updatedComment.id || isUpdatingReadStatus) return;

    setIsUpdatingReadStatus(true);

    // Hoisted out of the try block: the React Compiler cannot lower optional chaining, `??` or a
    // ternary inside try/catch, and bails on the whole component when it finds one.
    const isViewingAsVendor = session.context?.viewingAsVendor ?? false;
    const failureMessage = readOnly ? "Failed to mark comment as read." : "Failed to update comment read status.";
    const errorMessage = readOnly ? "Error marking comment as read:" : "Error updating comment read status:";

    try {
      const returnedMessages = await dispatch(
        messagingApi.endpoints.createBulkMessages.initiate({
          messages: [
            updatedComment.toMessage({
              isViewingAsVendor,
            }),
          ],
        }),
      ).unwrap();
      if (returnedMessages.length < 1) {
        console.error(failureMessage);
      } else {
        const returnedComment = Comment.fromMessage(returnedMessages[0]);
        dispatch(addComment(returnedComment));
        dispatch(addCommentThread(returnedComment.thread));
      }
    } catch (error) {
      console.error(errorMessage, error);
    }

    // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
    // this component. The early return above became an `else` so this is still reached on every path.
    setIsUpdatingReadStatus(false);
  }

  function handleCommentItemClicked() {
    if (!isUnread || isUpdatingReadStatus) return;

    void handleReadStatusChanged(comment.markForReadReceipt());
  }

  async function handlePublishStateChanged(publish: boolean) {
    if (isChangingPublishState) return;

    setIsChangingPublishState(true);

    // Hoisted for the same reason as in `handleReadStatusChanged` above.
    const publishAction = publish ? publishComment : unpublishComment;
    const publishErrorMessage = publish ? "Error publishing comment:" : "Error unpublishing comment:";
    const publishFailureMessage = publish
      ? "We couldn't publish your comment."
      : "We couldn't retract your comment.";

    try {
      await dispatch(publishAction({ session, comment })).unwrap();
    } catch (error) {
      console.error(publishErrorMessage, error);
      enqueueSnackbar(publishFailureMessage, { variant: "error" });
    }

    setIsChangingPublishState(false);
  }

  /**
   * Commits the row's open edit — the author's Save for this comment, and the only thing that commits it.
   *
   * The edit is written here rather than staged for a proposal save: a comment's audience is reading the
   * old text until the write lands, and making them wait on a save the author may never perform is what
   * tied the conversation to the negotiation's own Save button. The draft is what goes on the wire, so
   * the audience the author re-aimed the row at travels with their new text — and cancelling instead
   * abandons both.
   */
  async function handleEditSaved(edit: OpenCommentEdit) {
    if (edit.text.trim() === "" || isPersistingEdit) return;

    setIsPersistingEdit(true);
    try {
      await dispatch(editComment({ session, comment: edit.draft.edit(edit.text) })).unwrap();
      setOpenEdit(null);
    } catch (error) {
      console.error("Error saving comment edit:", error);
      // The row stays open on failure: the author's text — and their audience choice — are still theirs
      // to retry or abandon rather than silently lost.
      enqueueSnackbar("We couldn't save your comment.", { variant: "error" });
    }

    // Ran as a `finally` clause until the React Compiler, which cannot lower one, started skipping
    // this component. Neither branch above returns early, so this is still reached on every path.
    setIsPersistingEdit(false);
  }

  /**
   * Deletes the comment outright.
   *
   * It used to be staged behind a "Pending Deletion" chip that only a proposal save resolved, so a
   * comment its author had deleted stayed readable by its audience until — and unless — they saved the
   * proposal. Pressing Delete deletes. There is deliberately no undo: the deletion reaches the audience
   * at once, and the server has no restore (see ROADMAP.md's open question).
   */
  async function handleCommentDeleted() {
    if (isDeleting) return;

    setIsDeleting(true);
    try {
      await dispatch(deleteComment({ session, comment })).unwrap();
    } catch (error) {
      console.error("Error deleting comment:", error);
      enqueueSnackbar("We couldn't delete your comment.", { variant: "error" });
      setIsDeleting(false);
    }
  }

  function handleMarkReadKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key !== KeyboardKey.Enter && event.key !== KeyboardKey.Space) return;

    event.preventDefault();
    handleCommentItemClicked();
  }

  function renderReadUnreadButton(comment: Comment): React.ReactNode {
    if (readOnly && !isUnread) {
      return null;
    }

    const title = readOnly || isUnread ? "Mark Read" : "Mark Unread";
    const updatedComment = isUnread ? comment.markForReadReceipt() : comment.markForUnread();

    let readStatusIcon: React.ReactNode = <MarkEmailRead fontSize="inherit" />;
    if (!readOnly && isUnread) {
      readStatusIcon = <MarkEmailUnread fontSize="inherit" />;
    }

    return (
      <Tooltip title={title}>
        <IconButton
          aria-label={title}
          color="primary"
          disabled={isUpdatingReadStatus}
          size="small"
          style={{ alignContent: "right", float: "right" }}
          onClick={(event) => {
            event.stopPropagation();
            void handleReadStatusChanged(updatedComment);
          }}
        >
          {readStatusIcon}
        </IconButton>
      </Tooltip>
    );
  }

  const commentListItemProps = isUnread
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handleCommentItemClicked,
        onKeyDown: handleMarkReadKeyDown,
      }
    : {};
  const commentListItem = (
    <CommentListItem
      className={className}
      key={comment.id?.value ?? Guid.generate().value}
      pending={comment.isPending}
      unread={isUnread}
      disableGutters
      alignItems="flex-start"
      data-testid="comment-list-item"
      {...commentListItemProps}
    >
      <ListItemAvatar>
        {commenter?.isVisible ? (
          <Tooltip title="View profile">
            <AvatarButton
              aria-label={`View ${commenter.fullName}'s profile`}
              size="small"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleViewCommenterProfile();
              }}
            >
              <IndividualAvatar individual={commenter} />
            </AvatarButton>
          </Tooltip>
        ) : (
          <IndividualAvatar individual={commenter ?? undefined} />
        )}
      </ListItemAvatar>
      {!readOnly && openEdit && (
        <EditCommentInput
          multiline
          rows={4}
          variant="outlined"
          fullWidth
          value={openEdit.text}
          // The switch is drawn against the draft, not the shown comment, so re-aiming it is a change
          // to this edit and nothing else until the author saves.
          helperText={renderAudienceSwitch?.({
            comment: openEdit.draft,
            onCommentReaimed: (reaimed) => setOpenEdit({ ...openEdit, draft: reaimed }),
          })}
          slotProps={{
            input: {
              endAdornment: (
                <>
                  <Tooltip title="Save comment">
                    <IconButton
                      aria-label="Save comment"
                      color="primary"
                      size="small"
                      disabled={openEdit.text.trim() === "" || isPersistingEdit}
                      onClick={() => void handleEditSaved(openEdit)}
                    >
                      <SaveIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Cancel editing">
                    <IconButton
                      aria-label="Cancel editing"
                      color="primary"
                      size="small"
                      disabled={isPersistingEdit}
                      onClick={() => setOpenEdit(null)}
                    >
                      <CancelIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          }}
          onKeyDown={(event) => {
            if (event.key === KeyboardKey.Enter && openEdit.text.trim() !== "") {
              event.preventDefault();
              // Enter is the same act as pressing Save, so it commits and closes the row. It used to
              // only stage the edit and leave the editor open, which read as nothing having happened.
              void handleEditSaved(openEdit);
            } else if (event.key === KeyboardKey.Escape) {
              setOpenEdit(null);
            }
          }}
          onChange={(event) => setOpenEdit({ ...openEdit, text: event.target.value })}
        />
      )}
      {!openEdit && comment.senderId && (
        <ListItemText
          primary={
            <>
              <NameContainer>
                {(showPendingIndicator || isUnread) && (
                  <StatusIndicators>
                    {showPendingIndicator && (
                      <StatusDot
                        aria-hidden="true"
                        color="pending"
                        alert={isPendingExternalComment ? "error" : undefined}
                        data-testid="pending-comment-dot"
                      />
                    )}
                    {isUnread && <StatusDot aria-hidden="true" color="unread" data-testid="unread-comment-dot" />}
                  </StatusIndicators>
                )}
                <NameLink
                  disabled={!commenter?.isVisible}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!commenter?.isVisible) return;
                    handleViewCommenterProfile();
                  }}
                >
                  {commenter?.fullName}
                </NameLink>
              </NameContainer>
              {commentStatusItems.length > 0 && (
                <CommentStatusChip
                  label={
                    <>
                      <span className="comment-status-chip-icons" aria-hidden="true">
                        {commentStatusItems.map((item) => item.icon)}
                      </span>
                      <span data-testid="comment-status-labels">
                        {commentStatusItems.map((item) => item.label).join(", ")}
                      </span>
                    </>
                  }
                  size="small"
                  statusColor={statusChipColor}
                  variant="outlined"
                  data-testid="comment-status-chip"
                />
              )}
              {/*
                * A retraction tombstone carries its own caption in the body ("Removed at ..."), so the
                * row does not date it twice — and never as "Edited", which describes the text it is
                * standing in for rather than what happened to it.
                */}
              {/*
                * The control is a sibling of the caption, not a child of it. Nesting it put its
                * label inside CommentDate's own text, so an edited or retracted row read as one
                * string - "RemovedPublished: Sep 6, 2026 11:09 PM" - which a screen reader
                * announces as a single mangled phrase and no caption assertion can anchor to.
                * The two say different things: one is what happened to the comment, the other is
                * when. See #4750, which introduced the nesting, and #4806/#4807, which moved two
                * tests onto the welded string before this was recognised as the defect.
                */}
              {messageForHistory && historyLabel && (
                <MessageHistoryControl
                  message={messageForHistory}
                  label={historyLabel}
                  canViewHistory={canViewHistory}
                  surface="proposalComment"
                />
              )}
              {!comment.isRetractedTombstone && (
                <CommentDate variant="caption">
                  {comment.updated
                    ? "Edited: " + comment.updated.format("MMM D, YYYY h:mm A")
                    : comment.published
                      ? "Published: " + comment.published.format("MMM D, YYYY h:mm A")
                      : new AHDate().format("MMM D, YYYY h:mm A")}
                </CommentDate>
              )}
            </>
          }
          secondary={getCommentContent(comment)}
          slotProps={{
            // Render both slots as <div>: the secondary wraps parsed comment HTML that can emit block
            // <p> tags, which inside MUI's default <p> Typography triggers a "<p> cannot be a descendant
            // of <p>" hydration error. (The timeline panel does the same for the same reason.)
            primary: {
              component: "div",
              sx: {
                fontWeight: 500,
              },
            },
            secondary: {
              component: "div",
              sx: {
                color: "textPrimary",
              },
            },
          }}
        />
      )}
      {comment.senderId && !comment.senderId.isEqualTo(session.user?.id) && renderReadUnreadButton(comment)}
      {!readOnly && !comment.isDeleted && !comment.isRetractedTombstone && isOwnComment && (
          <>
            {!openEdit && !comment.isDeleted && (
              <>
                {comment.isUnpublishedDraft && (
                  <Tooltip title="Publish comment">
                    <IconButton
                      aria-label="Publish comment"
                      color="primary"
                      size="small"
                      disabled={isChangingPublishState}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handlePublishStateChanged(true);
                      }}
                    >
                      <SendIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                {comment.isVisibleToAudience && !comment.markedForUnpublish && !comment.markedForCreation && (
                  <Tooltip title="Unpublish comment">
                    <IconButton
                      aria-label="Unpublish comment"
                      color="primary"
                      size="small"
                      disabled={isChangingPublishState}
                      onClick={(event) => {
                        event.stopPropagation();
                        void handlePublishStateChanged(false);
                      }}
                    >
                      <UnpublishedIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit comment">
                  <IconButton
                    aria-label="Edit comment"
                    color="primary"
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenEdit({ draft: comment, text: comment.content ?? "" });
                    }}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                {/*
                  The only action in this row that is not `color="primary"`: it rests neutral and
                  goes red under the cursor, like every other inline delete in the app. It used to be
                  a red glyph inside a primary button, which tinted the ripple blue around a red icon
                  — the one combination neither convention asks for.
                */}
                <Tooltip title="Delete comment">
                  <DeleteIconButton
                    aria-label="Delete comment"
                    size="small"
                    disabled={isDeleting}
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleCommentDeleted();
                    }}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </DeleteIconButton>
                </Tooltip>
              </>
            )}
          </>
        )}
    </CommentListItem>
  );
  const systemCommentContentProps = isUnread
    ? {
        role: "button",
        tabIndex: 0,
        onClick: handleCommentItemClicked,
        onKeyDown: handleMarkReadKeyDown,
      }
    : {};

  return comment.senderId ? (
    commentListItem
  ) : (
    <SystemCommentContainer unread={isUnread}>
      {readOnly ? (
        <>
          <SystemCommentInfo style={{ justifyContent: "center" }}>
            <SystemCommentDateContainer>
              {isUnread && <StatusDot aria-hidden="true" color="unread" data-testid="unread-comment-dot" />}
              <SystemCommentTime>
                <time>{comment.published?.format("MM/DD/YY hh:mm A")}</time>
              </SystemCommentTime>
            </SystemCommentDateContainer>
          </SystemCommentInfo>
          <SystemMessageContent
            className="system-message-content"
            unread={isUnread}
            data-testid="system-message-content"
            {...systemCommentContentProps}
          >
            <ReactMarkdown>{comment.content}</ReactMarkdown>
          </SystemMessageContent>
        </>
      ) : (
        <>
          <SystemMessageContent
            className="system-message-content"
            unread={isUnread}
            data-testid="system-message-content"
            {...systemCommentContentProps}
          >
            <ReactMarkdown>{comment.content}</ReactMarkdown>
          </SystemMessageContent>
          <SystemCommentInfo>
            <SystemCommentDateContainer>
              {isUnread && <StatusDot aria-hidden="true" color="unread" data-testid="unread-comment-dot" />}
              <SystemCommentTime>
                <time>{comment.published?.format("MM/DD/YY hh:mm A")}</time>
              </SystemCommentTime>
            </SystemCommentDateContainer>
            {renderReadUnreadButton(comment)}
          </SystemCommentInfo>
        </>
      )}
    </SystemCommentContainer>
  );
}

/**
 * What a comment's pending chip says.
 *
 * There is only one thing it can mean now: a comment that has not reached its audience — an author-only
 * draft, or one still on the wire. Edits and deletions used to be pending too, held in the store behind
 * a chip until a proposal save sent them; each of those writes when its author performs it, so neither
 * ever waits for anything a reader could be told about.
 */
function getPendingStatus(): string {
  return "Pending";
}
